<template>
  <div
    class="modal fade"
    id="InteractiveHelpModal"
    tabindex="-1"
    aria-hidden="true"
    ref="helpModalRef"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content rounded-4 border-0 shadow-lg overflow-hidden help-modal-container">
        
        <!-- Compact Sleek Header with Integrated Search -->
        <div class="modal-header bg-gradient-help py-3 px-4 px-md-5 border-0 text-white d-flex align-items-center justify-content-between flex-shrink-0">
          <div class="d-flex align-items-center gap-3">
            <div class="help-header-icon shadow-xs">
              <i class="fa-solid fa-headset fs-4"></i>
            </div>
            <div>
              <div class="d-flex align-items-center gap-2">
                <h5 class="modal-title fw-bolder text-white mb-0 fs-5">Centru de Asistență & Ghid Interactiv</h5>
                <span class="badge bg-white bg-opacity-20 text-white fs-9 px-2 py-0.5 rounded-pill">v2.5</span>
              </div>
              <span class="text-white text-opacity-75 fs-8 d-none d-sm-inline">Ghiduri pas-cu-pas, întrebări frecvente, checklist și instrumente fiscale</span>
            </div>
          </div>

          <div class="d-flex align-items-center gap-3">
            <!-- Search bar inside header to save vertical space -->
            <div class="position-relative d-none d-md-block" style="width: 250px;">
              <i class="fa-solid fa-magnifying-glass text-white text-opacity-50 position-absolute top-50 start-0 translate-middle-y ms-3 fs-8"></i>
              <input
                v-model="searchQuery"
                type="text"
                class="form-control form-control-sm bg-white bg-opacity-15 border-0 text-white ps-8 pe-7 rounded-pill fs-8 help-header-search"
                placeholder="Caută în ghiduri / întrebări..."
                @input="onSearchInput"
              />
              <button
                v-if="searchQuery"
                class="btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y text-white text-opacity-75 me-1"
                @click="searchQuery = ''"
              >
                <i class="fa-solid fa-xmark fs-8"></i>
              </button>
            </div>

            <button
              type="button"
              class="btn btn-sm btn-icon text-white bg-white bg-opacity-15 rounded-circle hover-scale"
              data-bs-dismiss="modal"
              aria-label="Închide"
            >
              <i class="fa-solid fa-xmark fs-5"></i>
            </button>
          </div>
        </div>

        <!-- Modal Body: Two-Column Flex Layout -->
        <div class="modal-body p-0 d-flex flex-row help-body-flex">
          
          <!-- Left Navigation Menu -->
          <div class="help-sidebar-menu border-end bg-light-subtle p-3 d-flex flex-column justify-content-between flex-shrink-0">
            <ul class="nav nav-pills flex-column gap-2" role="tablist">
              <!-- 1. Întrebări Frecvente -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'intrebari' }"
                  @click="activeTab = 'intrebari'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-primary text-primary me-3">
                    <i class="fa-solid fa-circle-question fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Întrebări Frecvente</span>
                    <span class="menu-item-subtitle text-truncate">Răspunsuri rapide</span>
                  </div>
                </a>
              </li>

              <!-- 2. Checklist Lunar -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'checklist' }"
                  @click="activeTab = 'checklist'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-success text-success me-3">
                    <i class="fa-solid fa-list-check fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Checklist Lunar</span>
                    <span class="menu-item-subtitle text-truncate">Flux lună pas-cu-pas</span>
                  </div>
                  <span class="badge badge-sm bg-light-success text-success border border-success-subtle font-monospace ms-1">
                    {{ checklistCompletedCount }}/{{ checklistItems.length }}
                  </span>
                </a>
              </li>

              <!-- 3. Ghiduri Pas-cu-Pas -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'ghiduri' }"
                  @click="activeTab = 'ghiduri'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-info text-info me-3">
                    <i class="fa-solid fa-book-open fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Ghiduri Detaliate</span>
                    <span class="menu-item-subtitle text-truncate">Tutoriale complete</span>
                  </div>
                </a>
              </li>

              <!-- 4. Calculator & Termene Fiscale -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'unelte' }"
                  @click="activeTab = 'unelte'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-warning text-warning me-3">
                    <i class="fa-solid fa-calculator fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Calculator TVA & Termene</span>
                    <span class="menu-item-subtitle text-truncate">TVA și termene ANAF</span>
                  </div>
                </a>
              </li>

              <!-- 5. Scurtături Rapide & Tastatură -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'scurtaturi' }"
                  @click="activeTab = 'scurtaturi'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-danger text-danger me-3">
                    <i class="fa-solid fa-bolt fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Scurtături Rapide</span>
                    <span class="menu-item-subtitle text-truncate">Acces direct la module</span>
                  </div>
                </a>
              </li>

              <!-- 6. Suport & Contact -->
              <li class="nav-item">
                <a
                  class="nav-link d-flex align-items-center py-2.5 px-3 rounded-3"
                  :class="{ active: activeTab === 'contact' }"
                  @click="activeTab = 'contact'"
                  role="tab"
                  style="cursor: pointer;"
                >
                  <div class="nav-icon-box bg-light-success text-success me-3">
                    <i class="fa-solid fa-envelope-open-text fs-6"></i>
                  </div>
                  <div class="d-flex flex-column flex-grow-1 min-w-0 ms-1">
                    <span class="menu-item-title text-truncate">Suport & Contact</span>
                    <span class="menu-item-subtitle text-truncate">Asistență directă</span>
                  </div>
                </a>
              </li>
            </ul>

            <!-- Bottom info box -->
            <div class="mt-3 p-2.5 rounded-3 bg-body border shadow-2xs text-center">
              <i class="fa-solid fa-shield-halved text-primary fs-4 mb-1"></i>
              <div class="fw-bold text-gray-900 fs-9">Conta2GO v2.5</div>
              <div class="text-muted fs-9" style="font-size: 0.72rem !important;">ANAF SPV & Legislație 2026</div>
            </div>
          </div>

          <!-- Right Content Area -->
          <div class="help-main-content flex-grow-1 d-flex flex-column overflow-hidden">
            
            <!-- ============================================== -->
            <!-- 1. ÎNTREBĂRI FRECVENTE & RĂSPUNSURI RAPIDE     -->
            <!-- ============================================== -->
            <div v-if="activeTab === 'intrebari'" class="d-flex flex-column h-100 overflow-hidden">
              
              <!-- Tab Header -->
              <div class="px-4 py-3 border-bottom d-flex justify-content-between align-items-center bg-body flex-shrink-0">
                <div class="d-flex align-items-center gap-2.5">
                  <div class="symbol symbol-32px symbol-circle bg-light-primary text-primary d-flex align-items-center justify-content-center">
                    <i class="fa-solid fa-circle-question fs-6"></i>
                  </div>
                  <div>
                    <h6 class="text-gray-900 fw-bold m-0 fs-6">Întrebări Frecvente & Răspunsuri Rapide</h6>
                    <small class="text-muted fs-9">Alege o întrebare frecventă sau caută după cuvinte-cheie</small>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    class="btn btn-xs fw-bold"
                    :class="showAllFaqsList ? 'btn-primary' : 'btn-light-secondary'"
                    @click="showAllFaqsList = !showAllFaqsList"
                  >
                    <i class="fa-solid" :class="showAllFaqsList ? 'fa-comments me-1' : 'fa-list-ul me-1'"></i>
                    {{ showAllFaqsList ? 'Mod Dialog Rapid' : 'Toate Întrebările' }}
                  </button>
                  <button v-if="!showAllFaqsList" class="btn btn-xs btn-light fw-bold text-gray-700" @click="resetChat" title="Resetează dialogul">
                    <i class="fa-solid fa-rotate-left me-1"></i> Reset
                  </button>
                </div>
              </div>

              <!-- Content when showAllFaqsList is TRUE: Full categorized FAQ list -->
              <div v-if="showAllFaqsList" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
                <div class="mb-4 pb-2 border-bottom d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="text-gray-900 fw-bolder m-0 fs-6">Bază de Întrebări Frecvente (FAQ)</h6>
                    <span class="text-muted fs-8">Catalogul complet de întrebări și proceduri uzuale</span>
                  </div>
                  <span class="badge bg-light text-muted">{{ filteredFaqs.length }} întrebări</span>
                </div>

                <div class="d-flex flex-column gap-3">
                  <div
                    v-for="(faq, fIndex) in filteredFaqs"
                    :key="fIndex"
                    class="p-3.5 rounded-3 border bg-body shadow-2xs hover-elevate-up transition-all"
                  >
                    <div class="d-flex align-items-start gap-3">
                      <div class="symbol symbol-32px symbol-circle bg-light-primary text-primary d-flex align-items-center justify-content-center flex-shrink-0 mt-0.5">
                        <i class="fa-solid fa-circle-question fs-6"></i>
                      </div>
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center justify-content-between mb-1">
                          <h6 class="text-gray-900 fw-bold fs-7 mb-0">{{ faq.question }}</h6>
                          <span class="badge bg-light text-primary fs-9 fw-semibold">{{ faq.category }}</span>
                        </div>
                        <p class="text-gray-600 fs-7 mb-2">{{ faq.answer }}</p>
                        <button
                          type="button"
                          class="btn btn-xs btn-light-primary fw-bold"
                          @click="showAllFaqsList = false; askAssistant(faq.question)"
                        >
                          <i class="fa-solid fa-arrow-right fs-9 me-1"></i> Deschide răspunsul interactiv
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Content when showAllFaqsList is FALSE: Interactive Stream -->
              <template v-else>
                <!-- Messages Stream (Scrollable) -->
                <div class="chat-stream flex-grow-1 p-3 p-md-4 overflow-y-auto d-flex flex-column gap-3" ref="chatStreamRef">
                  <div
                    v-for="(msg, mIdx) in chatMessages"
                    :key="mIdx"
                    class="chat-row"
                    :class="msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'"
                  >
                    <!-- Help Avatar (Fixed 36x36 circular, never stretched!) -->
                    <div v-if="msg.sender !== 'user'" class="chat-avatar bg-primary text-white shadow-2xs">
                      <i class="fa-solid fa-circle-question fs-6"></i>
                    </div>

                    <!-- Message Bubble -->
                    <div :class="msg.sender === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'">
                      <div class="fs-7" v-html="msg.text"></div>

                      <!-- Direct Action Button inside Message -->
                      <div v-if="msg.actionRoute" class="mt-3 pt-2 border-top border-gray-200 border-opacity-50">
                        <button
                          type="button"
                          class="btn btn-sm btn-success fw-bold d-flex align-items-center gap-2 shadow-xs"
                          @click="navigateAndClose(msg.actionRoute, msg.actionTour)"
                        >
                          <i class="fa-solid fa-arrow-up-right-from-square fs-8"></i>
                          <span>{{ msg.actionText || 'Deschide Ecranul Recomandat' }}</span>
                        </button>
                      </div>

                      <!-- Tips Pill inside Message -->
                      <div v-if="msg.tips && msg.tips.length" class="mt-2.5 pt-2 border-top border-gray-100 fs-8 text-muted d-flex align-items-start gap-1.5">
                        <i class="fa-solid fa-lightbulb text-warning mt-0.5"></i>
                        <span><span class="fw-semibold text-gray-700">Sfat:</span> {{ msg.tips.join(' ') }}</span>
                      </div>
                    </div>

                    <!-- User Avatar -->
                    <div v-if="msg.sender === 'user'" class="chat-avatar bg-secondary text-dark shadow-2xs">
                      <i class="fa-solid fa-user fs-7"></i>
                    </div>
                  </div>

                  <!-- Searching Indicator -->
                  <div v-if="isBotTyping" class="chat-row justify-content-start align-items-center gap-2 text-muted small ps-1">
                    <div class="chat-avatar bg-primary text-white shadow-2xs">
                      <i class="fa-solid fa-circle-question fs-6"></i>
                    </div>
                    <div class="p-2 px-3 rounded-pill bg-light border text-muted fs-8 d-flex align-items-center gap-2">
                      <span class="spinner-grow spinner-grow-sm text-primary" role="status"></span>
                      <span>Se caută răspunsul în ghiduri...</span>
                    </div>
                  </div>
                </div>

                <!-- Suggested Questions (Single-line Compact Chips) -->
                <div class="chat-suggestions py-2 px-3 border-top bg-light-subtle flex-shrink-0">
                  <div class="d-flex align-items-center gap-1.5 overflow-x-auto prompt-scroll pb-1">
                    <span class="text-muted fs-9 fw-bold text-nowrap me-1">Sugestii:</span>
                    <button
                      v-for="(p, pIdx) in quickPrompts"
                      :key="pIdx"
                      type="button"
                      class="btn btn-xs btn-white border shadow-2xs text-nowrap rounded-pill px-2.5 py-1 text-gray-800 hover-elevate-up d-flex align-items-center gap-1.5 fs-8"
                      @click="askAssistant(p.prompt)"
                    >
                      <i :class="p.icon" class="text-primary fs-9"></i>
                      <span>{{ p.shortLabel }}</span>
                    </button>
                  </div>
                </div>

                <!-- Input Prompt Bar (Solidly Anchored at Bottom, Always Visible) -->
                <div class="chat-input-bar p-3 border-top bg-body flex-shrink-0">
                  <form @submit.prevent="submitAssistantQuery" class="d-flex gap-2 align-items-center">
                    <div class="position-relative flex-grow-1">
                      <i class="fa-regular fa-comment-dots text-gray-400 position-absolute top-50 start-0 translate-middle-y ms-3 fs-6"></i>
                      <input
                        v-model="assistantInput"
                        type="text"
                        class="form-control form-control-sm form-control-solid ps-9 pe-3 rounded-pill fs-7"
                        placeholder="Caută o întrebare (ex: finalizare lună, extras bancar, facturi cumpărări, culori luni)..."
                        :disabled="isBotTyping"
                      />
                    </div>
                    <button
                      type="submit"
                      class="btn btn-sm btn-primary rounded-pill px-3 py-2 fw-bold d-flex align-items-center gap-1.5 shadow-xs flex-shrink-0"
                      :disabled="!assistantInput.trim() || isBotTyping"
                    >
                      <span>Caută</span>
                      <i class="fa-solid fa-magnifying-glass fs-8"></i>
                    </button>
                  </form>
                </div>
              </template>

            </div>

            <!-- ============================================== -->
            <!-- 2. CHECKLIST LUNAR INTERACTIV                  -->
            <!-- ============================================== -->
            <div v-else-if="activeTab === 'checklist'" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div>
                  <h5 class="text-gray-900 fw-bolder m-0">
                    <i class="fa-solid fa-list-check text-success me-2"></i>Checklist Închidere Lună
                  </h5>
                  <span class="text-muted fs-8">Fluxul complet recomandat pentru pregătirea documentelor contabile</span>
                </div>
                <button class="btn btn-xs btn-outline-secondary" @click="resetChecklist">
                  <i class="fa-solid fa-rotate-left me-1"></i> Resetează bifările
                </button>
              </div>

              <!-- Progress Bar -->
              <div class="card p-3 bg-light rounded-3 border mb-4 shadow-2xs">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="fw-bold fs-7 text-gray-800">Progresul documentelor lunii curente:</span>
                  <span class="badge fs-8 fw-bold" :class="checklistProgress === 100 ? 'bg-success text-white' : 'bg-primary text-white'">
                    {{ checklistCompletedCount }} din {{ checklistItems.length }} pași ({{ checklistProgress }}%)
                  </span>
                </div>
                <div class="progress" style="height: 10px;">
                  <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    :class="checklistProgress === 100 ? 'bg-success' : 'bg-primary'"
                    role="progressbar"
                    :style="{ width: checklistProgress + '%' }"
                  ></div>
                </div>
                <div v-if="checklistProgress === 100" class="mt-2 text-success small fw-bold text-center">
                  🎉 Excelent! Toate etapele lunii sunt finalizate. Contabilul are toate datele necesare!
                </div>
              </div>

              <!-- Checklist Steps Interactive List -->
              <div class="d-flex flex-column gap-2.5">
                <div
                  v-for="(item, idx) in checklistItems"
                  :key="item.id"
                  class="p-3 rounded-3 border bg-body shadow-2xs d-flex align-items-start gap-3 transition-all hover-elevate-up"
                  :class="{ 'border-success bg-light-success border-opacity-75': item.done }"
                >
                  <!-- Checkbox -->
                  <div class="form-check form-check-custom form-check-solid mt-1">
                    <input
                      class="form-check-input cursor-pointer"
                      type="checkbox"
                      v-model="item.done"
                      @change="saveChecklistProgress"
                      :id="'chk_' + item.id"
                    />
                  </div>

                  <!-- Details -->
                  <div class="flex-grow-1">
                    <label :for="'chk_' + item.id" class="cursor-pointer mb-1 d-block">
                      <span class="fw-bold fs-6" :class="item.done ? 'text-decoration-line-through text-muted' : 'text-gray-900'">
                        Pasul {{ idx + 1 }}: {{ item.title }}
                      </span>
                    </label>
                    <div class="text-gray-600 fs-8">{{ item.desc }}</div>
                  </div>

                  <!-- Direct Action Button -->
                  <button
                    type="button"
                    class="btn btn-xs fw-bold text-nowrap align-self-center shadow-2xs"
                    :class="item.done ? 'btn-light-secondary text-muted' : 'btn-light-primary text-primary'"
                    @click="navigateAndClose(item.route)"
                  >
                    {{ item.btnText }} <i class="fa-solid fa-arrow-right fs-9 ms-1"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- ============================================== -->
            <!-- 3. GHIDURI PAS-CU-PAS                          -->
            <!-- ============================================== -->
            <div v-else-if="activeTab === 'ghiduri'" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
              <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <div>
                  <h5 class="text-gray-900 fw-bolder m-0">Tutoriale & Ghiduri Interactive</h5>
                  <span class="text-muted fs-8">{{ filteredGuides.length }} ghiduri disponibile</span>
                </div>
              </div>

              <!-- Category Filter Pills -->
              <div class="d-flex align-items-center gap-1.5 flex-wrap mb-4">
                <button
                  v-for="cat in guideCategories"
                  :key="cat"
                  type="button"
                  class="btn btn-xs fw-bold px-3 py-1.5 transition-all rounded-pill"
                  :class="selectedGuideCat === cat ? 'btn-primary shadow-xs' : 'btn-light text-gray-700'"
                  @click="selectedGuideCat = cat"
                >
                  {{ cat }}
                </button>
              </div>

              <div v-if="filteredGuides.length > 0" class="accordion" id="accordionHelpGuides">
                <div
                  v-for="guide in filteredGuides"
                  :key="guide.id"
                  class="accordion-item border rounded-3 mb-3 shadow-2xs overflow-hidden"
                >
                  <h2 class="accordion-header" :id="'heading_' + guide.id">
                    <button
                      class="accordion-button fw-bold fs-6 py-3 px-4"
                      :class="{ collapsed: openGuideId !== guide.id }"
                      type="button"
                      @click="toggleGuide(guide.id)"
                    >
                      <i :class="guide.icon + ' ' + guide.iconColor + ' me-3 fs-5'"></i>
                      <span class="text-gray-900 flex-grow-1">{{ guide.title }}</span>
                      <span class="badge badge-light-primary fw-semibold fs-8 me-3">{{ guide.category }}</span>
                    </button>
                  </h2>
                  <div
                    :id="'collapse_' + guide.id"
                    class="accordion-collapse collapse"
                    :class="{ show: openGuideId === guide.id }"
                  >
                    <div class="accordion-body p-4 bg-light-subtle">
                      <p class="text-gray-700 fs-7 mb-3">{{ guide.description }}</p>
                      
                      <div class="steps-list d-flex flex-column gap-2.5 mb-4">
                        <div
                          v-for="(step, sIdx) in guide.steps"
                          :key="sIdx"
                          class="d-flex align-items-start gap-3 bg-body p-3 rounded-2 border shadow-2xs"
                        >
                          <span class="badge badge-circle badge-primary fs-7 fw-bold">{{ sIdx + 1 }}</span>
                          <div class="flex-grow-1">
                            <strong class="text-gray-900 fs-7 d-block mb-1">{{ step.title }}</strong>
                            <span class="text-gray-600 fs-8">{{ step.text }}</span>
                          </div>
                        </div>
                      </div>

                      <div v-if="guide.tip" class="alert alert-light-warning border border-warning border-dashed py-2 px-3 mb-3 small d-flex align-items-center gap-2 rounded-2">
                        <i class="fa-solid fa-lightbulb text-warning fs-6"></i>
                        <span><strong>Sfat util:</strong> {{ guide.tip }}</span>
                      </div>

                      <div v-if="guide.actionRoute" class="d-flex justify-content-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-primary fw-bold"
                          @click="navigateAndClose(guide.actionRoute, guide.actionTour)"
                        >
                          {{ guide.actionText || 'Deschide Modulul' }} <i class="fa-solid fa-arrow-right fs-8 ms-1"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="text-center py-8 text-muted">
                <i class="fa-regular fa-folder-open fs-2x mb-3 text-gray-400"></i>
                <p class="fs-6">Nu am găsit ghiduri corespunzătoare căutării tale "{{ searchQuery }}".</p>
                <button class="btn btn-sm btn-light-primary" @click="searchQuery = ''; selectedGuideCat = 'Toate'">Resetează filtrele</button>
              </div>
            </div>

            <!-- ============================================== -->
            <!-- 4. CALCULATOR TVA & TERMENE FISCALE            -->
            <!-- ============================================== -->
            <div v-else-if="activeTab === 'unelte'" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
              <div class="mb-4 pb-2 border-bottom">
                <h5 class="text-gray-900 fw-bolder m-0">
                  <i class="fa-solid fa-calculator text-warning me-2"></i>Calculator TVA & Termene Lunare
                </h5>
                <span class="text-muted fs-8">Instrumente practice de calcul rapid și termenele importante pentru firma ta</span>
              </div>

              <!-- TVA Calculator Widget -->
              <div class="card border rounded-3 p-4 bg-body shadow-2xs mb-4">
                <h6 class="fw-bold text-gray-900 mb-3 d-flex align-items-center gap-2">
                  <i class="fa-solid fa-percent text-primary"></i> Calculator Rapid TVA
                </h6>

                <div class="row g-3">
                  <div class="col-md-5">
                    <label class="form-label fs-8 fw-bold">Valoare introdusă (RON)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      v-model.number="calcAmount"
                      class="form-control form-control-sm form-control-solid"
                      placeholder="Ex: 1000"
                    />
                  </div>
                  <div class="col-md-4">
                    <label class="form-label fs-8 fw-bold">Cotă TVA aplicabilă</label>
                    <select v-model.number="calcVatRate" class="form-select form-select-sm form-select-solid">
                      <option :value="21">21% (Cota standard generală)</option>
                      <option :value="19">19% (Cota anterioară)</option>
                      <option :value="9">9% (Alimente, apă, servicii Horeca, medicamente)</option>
                      <option :value="5">5% (Cărți, publicații, panouri solare)</option>
                      <option :value="0">0% (Scutit de TVA / Neimpozabil)</option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label fs-8 fw-bold">Tip sumă</label>
                    <div class="btn-group btn-group-sm w-100">
                      <button
                        type="button"
                        class="btn"
                        :class="calcMode === 'net' ? 'btn-primary' : 'btn-outline-secondary'"
                        @click="calcMode = 'net'"
                      >
                        Net
                      </button>
                      <button
                        type="button"
                        class="btn"
                        :class="calcMode === 'gross' ? 'btn-primary' : 'btn-outline-secondary'"
                        @click="calcMode = 'gross'"
                      >
                        Brut
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Results Box -->
                <div class="mt-4 p-3 rounded-3 bg-light border d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div>
                    <div class="text-muted small">Bază Impozabilă (Net):</div>
                    <span class="fs-5 fw-bold text-gray-900">{{ formatMoney(calcResults.net) }} RON</span>
                  </div>
                  <div>
                    <div class="text-muted small">TVA ({{ calcVatRate }}%):</div>
                    <span class="fs-5 fw-bold text-danger">{{ formatMoney(calcResults.vat) }} RON</span>
                  </div>
                  <div>
                    <div class="text-muted small">Total Factură (Brut):</div>
                    <span class="fs-4 fw-bolder text-success">{{ formatMoney(calcResults.total) }} RON</span>
                  </div>
                  <div>
                    <button class="btn btn-sm btn-light-primary fw-bold shadow-2xs" @click="copyCalcResult">
                      <i class="fa-solid fa-copy me-1"></i> Copiază
                    </button>
                  </div>
                </div>
              </div>

              <!-- Termene Importante pentru Antreprenor / Utilizator -->
              <div class="card border rounded-3 p-4 bg-body shadow-2xs">
                <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <h6 class="fw-bold text-gray-900 mb-0 d-flex align-items-center gap-2">
                    <i class="fa-solid fa-calendar-check text-success"></i> Calendarul Obligațiilor Tale Lunare (Firma Ta)
                  </h6>
                  <span class="badge bg-light-primary text-primary fs-9 px-2.5 py-1 fw-bold">Ghid Practic Utilizator</span>
                </div>

                <div class="alert alert-dismissible bg-light-primary border border-primary-subtle d-flex align-items-center p-3 mb-4 rounded-3">
                  <i class="fa-solid fa-circle-info fs-3 text-primary me-3"></i>
                  <div class="fs-8 text-gray-800">
                    <strong>Clarificare:</strong> Declarațiile fiscale complexe (cum ar fi D100, D112, D300, D394) sunt întocmite și depuse oficial la ANAF de către <strong>contabilul tău</strong>. Ca antreprenor / utilizator, tu ai de respectat doar pașii practici de mai jos:
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table table-sm table-row-dashed table-hover align-middle mb-0 fs-8">
                    <thead>
                      <tr class="fw-bold text-muted text-uppercase">
                        <th style="min-width: 170px;">Obligație / Activitate</th>
                        <th style="min-width: 250px;">Ce ai de făcut concret</th>
                        <th style="min-width: 170px;">Termen Limită</th>
                        <th style="min-width: 130px;">Unde rezolvi</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge badge-light-primary fw-bold p-2"><i class="fa-solid fa-paper-plane text-primary"></i></span>
                            <div>
                              <strong class="text-gray-900">Transmitere e-Factura</strong>
                              <div class="text-muted fs-9">Serverele ANAF SPV</div>
                            </div>
                          </div>
                        </td>
                        <td>Facturile emise către persoane juridice (B2B) sau instituții (B2G) trebuie trimise în sistemul național SPV.</td>
                        <td class="fw-bold text-danger">
                          <i class="fa-regular fa-clock me-1"></i> Maxim 5 zile calendaristice
                          <div class="text-muted fs-9 fw-normal">de la data emiterii facturii</div>
                        </td>
                        <td>
                          <button type="button" class="btn btn-xs btn-light-primary fw-bold" @click="navigateAndClose('/facturare')">
                            Facturi Emise <i class="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge badge-light-warning fw-bold p-2"><i class="fa-solid fa-cloud-arrow-up text-warning"></i></span>
                            <div>
                              <strong class="text-gray-900">Încărcare Facturi & Bonuri</strong>
                              <div class="text-muted fs-9">Documente furnizori</div>
                            </div>
                          </div>
                        </td>
                        <td>Încarcă facturile primite de la furnizori și bonurile fiscale cu CUI (combustibil, consumabile, etc.).</td>
                        <td class="fw-bold text-warning">
                          <i class="fa-regular fa-calendar me-1"></i> Până pe 5 - 10 ale lunii
                          <div class="text-muted fs-9 fw-normal">pentru luna încheiată</div>
                        </td>
                        <td>
                          <button type="button" class="btn btn-xs btn-light-warning fw-bold" @click="navigateAndClose('/incarcari?section=primite')">
                            Încarcă Facturi <i class="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge badge-light-info fw-bold p-2"><i class="fa-solid fa-file-invoice-dollar text-info"></i></span>
                            <div>
                              <strong class="text-gray-900">Extras Bancar</strong>
                              <div class="text-muted fs-9">PDF sau Excel din e-Banking</div>
                            </div>
                          </div>
                        </td>
                        <td>Descarcă extrasul contului bancar din online banking și încarcă-l în aplicație pentru reconciliere.</td>
                        <td class="fw-bold text-info">
                          <i class="fa-regular fa-calendar me-1"></i> Până pe 5 - 10 ale lunii
                          <div class="text-muted fs-9 fw-normal">după închiderea lunii bancare</div>
                        </td>
                        <td>
                          <button type="button" class="btn btn-xs btn-light-info fw-bold" @click="navigateAndClose('/incarcari?section=extras')">
                            Încarcă Extras <i class="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge badge-light-success fw-bold p-2"><i class="fa-solid fa-circle-check text-success"></i></span>
                            <div>
                              <strong class="text-gray-900">Marcare Lună Finalizată</strong>
                              <div class="text-muted fs-9">Notificare predare contabil</div>
                            </div>
                          </div>
                        </td>
                        <td>După ce ai urcat toate documentele și extrasele, apeși pe butonul verde "Am terminat încărcările".</td>
                        <td class="fw-bold text-success">
                          <i class="fa-regular fa-calendar-check me-1"></i> Imediat după încărcare
                          <div class="text-muted fs-9 fw-normal">deblochează lucrul contabilului</div>
                        </td>
                        <td>
                          <button type="button" class="btn btn-xs btn-light-success fw-bold" @click="navigateAndClose('/incarcari')">
                            Finalizare Lună <i class="fa-solid fa-arrow-right ms-1"></i>
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div class="d-flex align-items-center gap-2">
                            <span class="badge badge-light-danger fw-bold p-2"><i class="fa-solid fa-building-columns text-danger"></i></span>
                            <div>
                              <strong class="text-gray-900">Plata Taxelor la Buget</strong>
                              <div class="text-muted fs-9">ANAF & Bugetul de Stat</div>
                            </div>
                          </div>
                        </td>
                        <td>Plătește impozitele și contribuțiile conform sumelor calculate și comunicate de contabilul tău.</td>
                        <td class="fw-bold text-danger">
                          <i class="fa-solid fa-triangle-exclamation me-1"></i> Data de 25 a lunii
                          <div class="text-muted fs-9 fw-normal">termen legal ANAF pentru plată</div>
                        </td>
                        <td>
                          <span class="badge bg-light-danger text-danger border border-danger-subtle py-1.5 px-2">
                            Conform calcul contabil
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- ============================================== -->
            <!-- 5. SCURTĂTURI RAPIDE & TASTATURĂ               -->
            <!-- ============================================== -->
            <div v-else-if="activeTab === 'scurtaturi'" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
              <div class="mb-4 pb-2 border-bottom">
                <h5 class="text-gray-900 fw-bolder m-0">Navigare Rapidă către Module & Scurtături</h5>
                <span class="text-muted fs-8">Taste rapide și acces direct la cele mai frecvente funcționalități</span>
              </div>

              <!-- Keyboard shortcuts banner -->
              <div class="p-3 rounded-3 bg-light-primary border border-primary-subtle mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div class="d-flex align-items-center gap-3">
                  <div class="w-35px h-35px rounded-circle bg-white shadow-2xs d-flex align-items-center justify-content-center text-primary flex-shrink-0">
                    <i class="fa-regular fa-keyboard fs-5"></i>
                  </div>
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <strong class="fs-7 text-gray-900">Scurtături Tastatură:</strong>
                    <span class="fs-8 text-gray-800 fw-semibold">
                      Apasă <kbd class="shortcut-key">F1</kbd> oriunde pentru Asistență • <kbd class="shortcut-key">Esc</kbd> pentru închidere
                    </span>
                  </div>
                </div>
              </div>

              <div class="row g-3">
                <div
                  v-for="shortcut in shortcuts"
                  :key="shortcut.name"
                  class="col-md-6"
                >
                  <div
                    class="p-3 rounded-3 border bg-body shadow-2xs hover-elevate-up transition-all h-100 d-flex flex-column justify-content-between"
                    style="cursor: pointer;"
                    @click="navigateAndClose(shortcut.route)"
                  >
                    <div class="d-flex align-items-center gap-3 mb-2">
                      <div class="symbol symbol-35px symbol-circle" :class="'bg-light-' + shortcut.color">
                        <i :class="shortcut.icon + ' text-' + shortcut.color + ' fs-5'"></i>
                      </div>
                      <div>
                        <h6 class="text-gray-900 fw-bolder mb-0 fs-7">{{ shortcut.name }}</h6>
                        <span class="text-muted fs-8">{{ shortcut.desc }}</span>
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center pt-2 border-top border-gray-100">
                      <span class="badge badge-light-dark fs-9">{{ shortcut.module }}</span>
                      <span class="text-primary fw-bold fs-8">Deschide <i class="fa-solid fa-chevron-right fs-9 ms-1"></i></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            <!-- ============================================== -->
            <!-- 7. SUPORT & CONTACT                            -->
            <!-- ============================================== -->
            <div v-else-if="activeTab === 'contact'" class="p-4 p-md-5 overflow-y-auto flex-grow-1">
              <div class="mb-4 pb-2 border-bottom">
                <h5 class="text-gray-900 fw-bolder m-0">Contactează Echipa de Asistență</h5>
                <span class="text-muted fs-8">Suntem aici să te ajutăm cu orice întrebare tehnică sau contabilă</span>
              </div>

              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <div class="p-3 rounded-3 border bg-body text-center shadow-2xs">
                    <i class="fa-solid fa-envelope text-primary fs-2 mb-1"></i>
                    <div class="fw-bold text-gray-900 fs-7">Asistență prin Email</div>
                    <a href="mailto:suport@conta2go.ro" class="text-primary fw-semibold fs-8">suport@conta2go.ro</a>
                    <div class="text-muted fs-9 mt-0.5">Răspuns în maxim 24h</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="p-3 rounded-3 border bg-body text-center shadow-2xs">
                    <i class="fa-solid fa-phone text-success fs-2 mb-1"></i>
                    <div class="fw-bold text-gray-900 fs-7">Suport Telefonic</div>
                    <span class="text-gray-900 fw-bolder fs-8">0744 000 000</span>
                    <div class="text-muted fs-9 mt-0.5">Luni - Vineri, 09:00 - 17:00</div>
                  </div>
                </div>
              </div>

              <div class="card border rounded-3 p-4 bg-body shadow-2xs">
                <h6 class="text-gray-900 fw-bold mb-3">Trimite un mesaj direct:</h6>
                <div class="mb-3">
                  <label class="form-label fs-8 fw-bold">Subiect</label>
                  <input v-model="contactForm.subiect" type="text" class="form-control form-control-sm form-control-solid" placeholder="Ex: Întrebare privind seria facturilor sau conectarea SPV...">
                </div>
                <div class="mb-3">
                  <label class="form-label fs-8 fw-bold">Mesaj</label>
                  <textarea v-model="contactForm.mesaj" class="form-control form-control-sm form-control-solid" rows="3" placeholder="Detaliați problema sau întrebarea..."></textarea>
                </div>
                <button type="button" class="btn btn-sm btn-primary fw-bold align-self-start" @click="sendSupportMessage">
                  <i class="fa-solid fa-paper-plane me-1"></i> Trimite Solicitarea
                </button>
              </div>
            </div>

          </div>
        </div>

        <!-- Compact Footer -->
        <div class="modal-footer py-2.5 px-4 px-md-5 bg-light border-top d-flex justify-content-between align-items-center flex-shrink-0">
          <div class="text-gray-800 fs-8 fw-semibold d-flex align-items-center flex-wrap gap-1">
            <i class="fa-regular fa-keyboard text-primary me-1 fs-6"></i>
            <span>Apasă <kbd class="shortcut-key">F1</kbd> oriunde pentru Asistență • <kbd class="shortcut-key">Esc</kbd> pentru închidere</span>
          </div>
          <button type="button" class="btn btn-sm btn-light fw-bold" data-bs-dismiss="modal">
            Închide
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InteractiveHelpModal',
  data() {
    return {
      searchQuery: '',
      activeTab: 'intrebari',
      showAllFaqsList: false,
      openGuideId: 10,
      selectedGuideCat: 'Toate',
      guideCategories: ['Toate', 'Încărcare Documente', 'Finalizare Lună', 'Facturare Clienți', 'e-Factura ANAF', 'Încasări'],

      // Întrebări Frecvente Data
      assistantInput: '',
      isBotTyping: false,
      chatMessages: [
        {
          sender: 'assistant',
          text: '👋 <strong>Ghid & Întrebări Frecvente Conta</strong><br>Selectează una dintre întrebările frecvente de mai jos sau caută subiectul dorit în aplicație:',
          time: 'Acum',
          actionRoute: null,
          actionText: null,
          tips: ['Exemple: finalizare lună, extras bancar, facturi cumpărări, culori luni galben/roșu, emitere factură, conectare SPV.']
        }
      ],
      quickPrompts: [
        { shortLabel: 'Finalizare lună', icon: 'fa-solid fa-circle-check', prompt: 'Cum marchez luna ca finalizată când termin încărcările?' },
        { shortLabel: 'Extras de cont', icon: 'fa-solid fa-file-invoice', prompt: 'Cum încarc extrasul de cont bancar?' },
        { shortLabel: 'Facturi cumpărări', icon: 'fa-solid fa-cloud-arrow-up', prompt: 'Cum încarc facturile primite de la furnizori?' },
        { shortLabel: 'Culori luni (galben/roșu)', icon: 'fa-solid fa-triangle-exclamation', prompt: 'Ce înseamnă când o lună este galbenă sau roșie?' },
        { shortLabel: 'Emite factură nouă', icon: 'fa-solid fa-file-invoice-dollar', prompt: 'Cum emit o factură fiscală nouă către un client?' },
        { shortLabel: 'Conectare ANAF SPV', icon: 'fa-solid fa-building-columns', prompt: 'Cum conectez e-Factura ANAF la firma mea?' }
      ],

      // Checklist Lunar Data (100% Utilizator / Client)
      checklistItems: [
        { id: 1, title: 'Emitere facturi către clienți', desc: 'Emite toate facturile fiscale pentru livrările și serviciile lunii.', route: '/facturare/iesiri', btnText: 'Emite Facturi', done: false },
        { id: 2, title: 'Înregistrare încasări & chitanțe', desc: 'Încasează facturile prin casă (chitanțe) sau bancă (OP) și alocă sumele.', route: '/facturare', btnText: 'Vezi Facturi', done: false },
        { id: 3, title: 'Încărcare facturi cumpărări (furnizori)', desc: 'Încarcă facturile primite de la furnizori și bonurile fiscale cu CUI.', route: '/incarcari?section=primite', btnText: 'Încarcă Facturi', done: false },
        { id: 4, title: 'Încărcare Extras de cont bancar', desc: 'Descarcă extrasul lunii în format PDF sau Excel din online banking și încarcă-l.', route: '/incarcari?section=extras', btnText: 'Încarcă Extras', done: false },
        { id: 5, title: 'Verificare documente atașate', desc: 'Asigură-te că ai atașat toate actele lunii înainte de a notifica contabilul.', route: '/incarcari?section=primite', btnText: 'Verifică Acte', done: false },
        { id: 6, title: 'Predare lună la Contabil (Finalizare)', desc: 'Apasă butonul verde "Am terminat încărcările" pentru a notifica contabilul.', route: '/incarcari?section=primite', btnText: 'Finalizează Luna', done: false }
      ],

      // Calculator TVA Data
      calcAmount: 1000,
      calcVatRate: 21,
      calcMode: 'net',

      // Contact Form
      contactForm: {
        subiect: '',
        mesaj: ''
      },

      // Comprehensive Guides (Strictly Client / Utilizator Focused)
      guides: [
        {
          id: 10,
          title: 'Cum marchez luna ca finalizată când termin încărcările?',
          category: 'Finalizare Lună',
          icon: 'fa-solid fa-circle-check',
          iconColor: 'text-success',
          description: 'Notificarea contabilului că ai încărcat toate actele unei luni și luna este pregătită pentru prelucrare contabilă.',
          steps: [
            { title: 'Accesare modul Documente', text: 'Mergi în meniul lateral la Documente (sau apasă butonul de acțiune directă de mai jos).' },
            { title: 'Selectare Lună & Verificare fișiere', text: 'Selectează luna dorită din selectorul de sus (ex: August) și asigură-te că ai încărcat toate facturile și extrasele.' },
            { title: 'Apăsare buton finalizare', text: 'Sub selectorul de luni, pe bara verde, apasă butonul "Am terminat încărcările pe [Luna]".' },
            { title: 'Predare către contabil', text: 'Luna primește statusul FINALIZAT (galben), iar contabilul tău primește alertă că poate începe prelucrarea.' },
            { title: 'Anulare (dacă ai uitat un document)', text: 'Dacă ai uitat o factură, poți apăsa oricând "Anulează finalizarea (mai am documente)" pentru a redeschide luna.' }
          ],
          tip: 'Nu uita să încarci și extrasul bancar complet înainte de a preda luna contabilului!',
          actionRoute: '/incarcari?section=primite',
          actionText: 'Mergi la Documente (Finalizare)',
          actionTour: {
            target: '.btn-finalize-month',
            title: 'Finalizare Lună pentru Contabil',
            message: '👉 <strong>Apasă pe acest buton verde</strong> ("Am terminat încărcările") când ai încărcat toate actele lunii!',
            position: 'top'
          }
        },
        {
          id: 20,
          title: 'Cum încarc extrasul de cont bancar?',
          category: 'Încărcare Documente',
          icon: 'fa-solid fa-file-invoice',
          iconColor: 'text-info',
          description: 'Ghid rapid pentru încărcarea extraselor bancare lunare pentru reconcilierea plăților și încasărilor.',
          steps: [
            { title: 'Descărcare extras din Online Banking', text: 'Intră în contul băncii tale (BT, BCR, ING, Revolut etc.) și descarcă extrasul lunii în format PDF sau Excel (MT940 / CSV).' },
            { title: 'Deschidere secțiune Extras de cont', text: 'În modulul Documente, selectează secțiunea "Extras de cont" (se deschide automat prin acest ghid).' },
            { title: 'Selectare Lună & An', text: 'Alege luna căreia îi aparține extrasul descărcat din selectorul de date.' },
            { title: 'Încărcare fișier', text: 'Trage fișierul extrasului în zona de încărcare sau apasă "Încărcare fișiere". Fișierul va fi stocat securizat pentru contabil.' }
          ],
          tip: 'Poți încărca extrase pentru mai multe bănci în aceeași lună dacă firma ta are conturi la mai multe instituții bancare.',
          actionRoute: '/incarcari?section=extras',
          actionText: 'Încarcă Extras de Cont',
          actionTour: {
            target: '#btn-incarcari-extras, .btn-doc.active',
            title: 'Încărcare Extras de Cont',
            message: '👉 Secțiunea <strong>Extras de cont</strong> a fost selectată! Aici poți încărca extrasele bancare în format PDF sau Excel.',
            position: 'bottom'
          }
        },
        {
          id: 30,
          title: 'Cum încarc facturile primite de la furnizori (cumpărări)?',
          category: 'Încărcare Documente',
          icon: 'fa-solid fa-cloud-arrow-up',
          iconColor: 'text-primary',
          description: 'Încărcarea facturilor de achiziții și cheltuieli emise de furnizorii firmei tale.',
          steps: [
            { title: 'Accesare Documente Primite', text: 'Mergi la Documente -> secțiunea "Documente primite / cumpărări" (deschisă automat de butonul de mai jos).' },
            { title: 'Selectare Lună', text: 'Asigură-te că este selectată luna în care au fost emise facturile de furnizori.' },
            { title: 'Tragere fișiere (PDF / Scan)', text: 'Trage facturile scanate, PDF-urile sau bonurile fiscale cu CUI în zona de încărcare.' },
            { title: 'Disponibilitate imediată', text: 'Fișierele apar în lista lunii și devin disponibile imediat pentru contabilul tău.' }
          ],
          tip: 'Dacă furnizorul a trimis factura prin e-Factura ANAF și ai SPV conectat, factura va apărea automat și la Facturi Primite!',
          actionRoute: '/incarcari?section=primite',
          actionText: 'Încarcă Facturi Cumpărări',
          actionTour: {
            target: '#btn-incarcari-primite, .btn-doc.active',
            title: 'Facturi Primite (Cumpărări)',
            message: '👉 Secțiunea <strong>Documente primite</strong> a fost selectată! Trage facturile și bonurile de la furnizori aici.',
            position: 'bottom'
          }
        },
        {
          id: 40,
          title: 'Ce înseamnă culorile lunilor (Verde, Galben, Roșu)?',
          category: 'Finalizare Lună',
          icon: 'fa-solid fa-triangle-exclamation',
          iconColor: 'text-warning',
          description: 'Semnificația avertizărilor de stare ale lunilor calendaristice pentru tine ca utilizator.',
          steps: [
            { title: '🟢 Verde (Lună activă / în lucru)', text: 'Luna curentă și luna precedentă (M-1). Poți încărca documente și emite facturi în mod normal, fără avertismente de întârziere.' },
            { title: '🟡 Galben (Atenție - Se apropie termenul)', text: 'Luna de acum 2 luni (M-2, de exemplu Iulie când suntem în Septembrie). Se apropie scadența declarațiilor – contabilul are nevoie urgent de toate actele pentru a nu intra în depășire!' },
            { title: '🟡 Galben (Status FINALIZAT)', text: 'Ai apăsat butonul verde "Am terminat încărcările". Luna a fost predată și este în verificare la biroul de contabilitate.' },
            { title: '🔴 Roșu (Restantă M-3+)', text: 'Lună veche (mai veche de 2 luni) rămasă nefinalizată sau cu documente lipsă. Necesită atenție urgentă: intră pe luna respectivă, încarcă actele restante și finalizează luna.' },
            { title: '🔄 Ai uitat un document?', text: 'Până când contabilul închide luna definitiv, poți apăsa butonul "Anulează finalizarea" pentru a redeschide luna și a adăuga documentele lipsă.' }
          ],
          tip: 'Predarea documentelor la timp (până la data de 10-15 a fiecărei luni) evită apariția alertelor galbene sau roșii.',
          actionRoute: '/incarcari?section=primite',
          actionText: 'Vezi Situația Lunilor',
          actionTour: {
            target: '.btn-finalize-month, #btn-incarcari-primite',
            title: 'Gestiune Lună Documente',
            message: '👉 Aici poți verifica starea lunii și butonul de predare către contabil!',
            position: 'top'
          }
        },
        {
          id: 1,
          title: 'Cum emit o factură fiscală nouă către un client?',
          category: 'Facturare Clienți',
          icon: 'fa-solid fa-file-invoice-dollar',
          iconColor: 'text-success',
          description: 'Ghid pas-cu-pas pentru emiterea unei facturi către un client nou sau existent.',
          steps: [
            { title: 'Accesare modul', text: 'Mergi în meniul lateral la Facturare -> + Adaugă Factură (sau butonul rapid de mai jos).' },
            { title: 'Selectare Serie & Număr', text: 'Aplicația preia automat seria implicită a firmei și numărul imediat următor conform plajei de numere.' },
            { title: 'Alegere Client', text: 'Caută clientul după CUI sau Denumire. Dacă nu există, datele se descarcă automat de la ANAF.' },
            { title: 'Adăugare Articole / Produse', text: 'Selectează produsul, cantitatea, prețul unitar și cota TVA. Totalul și TVA-ul se calculează în timp real.' },
            { title: 'Salvare & Trimitere', text: 'Apasă "Salvează Factura". Factura este gata de descărcat PDF sau trimis pe e-mail clientului și în e-Factura.' }
          ],
          tip: 'Poți bifa "Trimite automat pe e-mail clientului" la salvare!',
          actionRoute: '/facturare/iesiri',
          actionText: 'Emite Factură Acum',
          actionTour: {
            target: '#formular_rapid, .card-body',
            title: 'Emitere Factură Fiscală',
            message: '👉 Completează datele clientului și articolele facturii, apoi apasă Salvează Factura!',
            position: 'bottom'
          }
        },
        {
          id: 2,
          title: 'Cum conectez e-Factura ANAF (SPV) la firma mea?',
          category: 'e-Factura ANAF',
          icon: 'fa-solid fa-building-columns',
          iconColor: 'text-primary',
          description: 'Integrarea directă cu serverele ANAF SPV pentru transmiterea și descărcarea facturilor electronice.',
          steps: [
            { title: 'Accesare Firmele Mele', text: 'Mergi în meniul lateral la Firmele Mele.' },
            { title: 'Deschidere Editare Firmă', text: 'În tabelul cu firmele tale, apasă pe iconița de editare (creion ✏️) din dreptul firmei dorite.' },
            { title: 'Conectare ANAF din fereastra de editare', text: 'În fereastra deschisă, în partea de jos (la subsol), apasă pe butonul albastru "🔐 Conectează ANAF".' },
            { title: 'Autorizare certificat digital', text: 'Vei fi redirecționat securizat pe portalul oficial ANAF pentru autorizarea cu certificatul tău digital SPV.' },
            { title: 'Verificare status', text: 'După autorizare, statusul va fi afișat verde "🟢 ANAF SPV Conectat" împreună cu data de expirare a token-ului.' }
          ],
          tip: 'La utilizator, conectarea se face direct pentru firma ta selectată (ARVINTE M. MIHAI-DANIEL ÎNTREPRINDERE INDIVIDUALĂ CUI: 52314766) din fereastra de editare firmă, la subsol!',
          actionRoute: '/firmele-mele?action=anaf',
          actionText: 'Deschide Conectare ANAF pentru firma ta',
          actionTour: {
            target: '#btn-conecteaza-anaf, .btn-active-firm-actions, #btn-firma-actions-52314766, .row-firma-cui-52314766 .btn-firma-actions, .row-firma-activa .btn-firma-actions, #tabel_firme tbody tr:first-child .btn-firma-actions',
            title: 'Conectare ANAF SPV',
            message: '👉 În fereastra deschisă pentru firma ta, apasă pe butonul <strong>🔐 Conectează ANAF</strong> din partea de jos pentru autorizarea cu certificatul digital!',
            position: 'top'
          }
        },
        {
          id: 3,
          title: 'Cum înregistrez o încasare pe o factură emisă (Chitanță sau Bancă)?',
          category: 'Încasări',
          icon: 'fa-solid fa-cash-register',
          iconColor: 'text-warning',
          description: 'Gestionarea chitanțelor de casă și a ordinelor de plată bancare pentru facturile tale.',
          steps: [
            { title: 'Încasare din lista de facturi', text: 'În ecranul Facturi Emise, apasă pe iconița verde de bancnotă 💵 din dreptul facturii dorite.' },
            { title: 'Alegere modalitate de încasare', text: 'Selectează dacă încasarea s-a făcut în numerar (Chitanță) sau prin contul bancar (Ordin de plată / Card).' },
            { title: 'Completare sumă & dată', text: 'Suma poate fi achitată integral sau parțial. Soldul rămas al clientului se recalculează automat.' },
            { title: 'Salvare încasare', text: 'Apasă Salvează. Factura își va schimba automat statusul în "Încasată" sau "Încasată parțial".' }
          ],
          tip: 'Dacă ai o plată parțială, soldul rămas va fi evidențiat automat cu galben.',
          actionRoute: '/facturare',
          actionText: 'Vezi Facturi Emise',
          actionTour: {
            target: '#tabel_facturi_emise tbody tr:first-child .btn, #tabel_facturi_emise',
            title: 'Facturi Emise & Încasări',
            message: '👉 Folosește iconița cu bancnotă 💵 din dreptul fiecărei facturi pentru a înregistra plăți și chitanțe.',
            position: 'bottom'
          }
        }
      ],

      shortcuts: [
        { name: 'Emite Factură Fiscală', desc: 'Factură nouă către clienți', route: '/facturare/iesiri', icon: 'fa-solid fa-plus', color: 'success', module: 'Facturare' },
        { name: 'Facturi Emise & Încasări', desc: 'Lista facturilor și chitanțe', route: '/facturare', icon: 'fa-solid fa-list', color: 'primary', module: 'Facturare' },
        { name: 'Încarcă Extras de Cont', desc: 'Extrase bancare lunare', route: '/incarcari?section=extras', icon: 'fa-solid fa-file-invoice', color: 'info', module: 'Documente' },
        { name: 'Încarcă Facturi Cumpărări', desc: 'Documente de la furnizori', route: '/incarcari?section=primite', icon: 'fa-solid fa-cloud-arrow-up', color: 'primary', module: 'Documente' },
        { name: 'Încarcă Alte Documente', desc: 'Contracte, decizii, diverse', route: '/incarcari?section=alte', icon: 'fa-solid fa-folder-open', color: 'secondary', module: 'Documente' },
        { name: 'Facturi Primite (Intrări)', desc: 'Lista achizițiilor și furnizorilor', route: '/intrari/facturi', icon: 'fa-solid fa-receipt', color: 'danger', module: 'Intrări' },
        { name: 'Firmele Mele & ANAF SPV', desc: 'Configurare date și conectare ANAF', route: '/firmele-mele?action=anaf', icon: 'fa-solid fa-building', color: 'success', module: 'Setări' },
        { name: 'Registru Încasări & Plăți', desc: 'Jurnal de casă și bancă', route: '/registru-incasari-plati', icon: 'fa-solid fa-cash-register', color: 'warning', module: 'Trezorerie' }
      ],

      faqs: [
        {
          question: 'Cum încarc extrasul bancar și în ce format trebuie să fie?',
          category: 'Încărcare Documente',
          answer: 'Descarcă extrasul de cont din online banking în format PDF, Excel, MT940 sau CSV. Intră în secțiunea Extras de cont, alege luna dorită și trage fișierul. Contabilul tău va prelua tranzacțiile direct pentru reconciliere.'
        },
        {
          question: 'Ce înseamnă când o lună are badge GALBEN?',
          category: 'Finalizare Lună',
          answer: 'Poate avea două semnificații pentru tine: 1) Dacă este o lună din urmă cu 2 luni (termen M-2), este un avertisment că se apropie termenul de declarații și contabilul are nevoie urgent de actele tale. 2) Dacă statusul este "FINALIZAT", înseamnă că ai apăsat "Am terminat încărcările" și luna a fost predată către contabil.'
        },
        {
          question: 'Ce înseamnă când o lună are badge ROȘU (Restantă)?',
          category: 'Finalizare Lună',
          answer: 'Indică o lună mai veche de 2 luni (M-3 sau mai veche) rămasă nefinalizată sau cu documente lipsă. Trebuie să intri urgent pe acea lună, să încarci actele restante și să apeși butonul verde "Am terminat încărcările".'
        },
        {
          question: 'Pot adăuga documente după ce am apăsat "Am terminat încărcările"?',
          category: 'Finalizare Lună',
          answer: 'Da! Până când contabilul închide definitiv luna, poți apăsa oricând butonul "Anulează finalizarea (mai am documente)" pentru a o redeschide și a adăuga facturile uitate.'
        },
        {
          question: 'Cum conectez firma la e-Factura ANAF (SPV)?',
          category: 'e-Factura ANAF',
          answer: 'Mergi la Firmele Mele, apasă pe butonul de Editare (creion ✏️) din dreptul firmei tale, iar în partea de jos a ferestrei apasă butonul albastru "🔐 Conectează ANAF". Vei fi redirecționat securizat la ANAF pentru autorizarea cu certificatul digital.'
        },
        {
          question: 'Cum se generează numărul următor la emiterea unei facturi?',
          category: 'Facturare Clienți',
          answer: 'Sistemul detectează automat ultimul număr emis pe seria activă a firmei și îl incrementează cu +1, astfel încât numerotarea să fie continuă și fără erori.'
        },
        {
          question: 'Cum înregistrez o încasare parțială pe o factură?',
          category: 'Încasări',
          answer: 'În lista Facturi Emise, apasă pe iconița de bancnotă 💵 din dreptul facturii, introdu suma efectiv plătită și alege Chitanță sau Bancă. Soldul restant va fi recalculat automat.'
        }
      ]
    };
  },
  computed: {
    checklistCompletedCount() {
      return this.checklistItems.filter(i => i.done).length;
    },
    checklistProgress() {
      if (!this.checklistItems.length) return 0;
      return Math.round((this.checklistCompletedCount / this.checklistItems.length) * 100);
    },
    calcResults() {
      const amount = Number(this.calcAmount) || 0;
      const rate = Number(this.calcVatRate) || 0;
      if (this.calcMode === 'net') {
        const net = amount;
        const vat = (net * rate) / 100;
        const total = net + vat;
        return { net, vat, total };
      } else {
        const total = amount;
        const net = rate > 0 ? (total / (1 + rate / 100)) : total;
        const vat = total - net;
        return { net, vat, total };
      }
    },
    filteredGuides() {
      let list = this.guides;
      if (this.selectedGuideCat !== 'Toate') {
        list = list.filter(g => g.category === this.selectedGuideCat);
      }
      if (!this.searchQuery) return list;
      const q = this.searchQuery.toLowerCase();
      return list.filter(g => 
        g.title.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        g.steps.some(s => s.title.toLowerCase().includes(q) || s.text.toLowerCase().includes(q))
      );
    },
    filteredFaqs() {
      if (!this.searchQuery) return this.faqs;
      const q = this.searchQuery.toLowerCase();
      return this.faqs.filter(f => 
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q)
      );
    }
  },
  mounted() {
    window.openHelpModal = (topic) => {
      this.openModal(topic);
    };

    window.addEventListener('keydown', this.handleGlobalKeydown);
    this.loadChecklistProgress();

    this.$nextTick(() => {
      const modalEl = document.getElementById('InteractiveHelpModal');
      if (modalEl) {
        modalEl.addEventListener('hide.bs.modal', () => {
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
        });
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    formatMoney(val) {
      if (val === undefined || val === null || isNaN(val)) return '0,00';
      return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    handleGlobalKeydown(e) {
      if (e.key === 'F1') {
        e.preventDefault();
        this.openModal();
      }
    },
    openModal(topic) {
      if (topic) {
        this.searchQuery = topic;
        this.activeTab = 'ghiduri';
      }
      if (window.$) {
        window.$('#InteractiveHelpModal').modal('show');
      }
    },
    closeModal() {
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
      if (window.$) {
        window.$('#InteractiveHelpModal').modal('hide');
      }
    },
    onSearchInput() {
      if (this.searchQuery.trim() && this.activeTab !== 'ghiduri' && this.activeTab !== 'intrebari') {
        this.activeTab = 'ghiduri';
      }
    },
    toggleGuide(id) {
      this.openGuideId = this.openGuideId === id ? null : id;
    },
    navigateAndClose(route, tourConfig) {
      this.closeModal();

      // Check if target route specifies a section parameter (e.g. /incarcari?section=extras)
      let targetSection = null;
      if (route && route.includes('section=')) {
        const match = route.match(/section=([a-zA-Z0-9_-]+)/);
        if (match && match[1]) {
          targetSection = match[1];
        }
      }

      // If no explicit tourConfig provided, deduce default target by route
      if (!tourConfig && route) {
        if (route.includes('/incarcari')) {
          if (targetSection === 'extras') {
            tourConfig = {
              target: '#btn-incarcari-extras, .btn-doc.active',
              title: 'Încărcare Extras de Cont',
              message: '👉 Secțiunea <strong>Extras de cont</strong> a fost deschisă automat! Trage sau alege extrasele lunii (PDF, Excel, MT940).',
              position: 'bottom'
            };
          } else if (targetSection === 'primite') {
            tourConfig = {
              target: '#btn-incarcari-primite, .btn-doc.active',
              title: 'Facturi Primite (Cumpărări)',
              message: '👉 Secțiunea <strong>Documente primite</strong> a fost deschisă automat! Încarcă aici facturile de la furnizori și bonurile fiscale.',
              position: 'bottom'
            };
          } else {
            tourConfig = {
              target: '.btn-finalize-month',
              title: 'Finalizare Lună pentru Contabil',
              message: '👉 <strong>Apasă pe acest buton verde</strong> când ai terminat de încărcat toate actele lunii pentru a notifica contabilul!',
              position: 'top'
            };
          }
        } else if (route.includes('/facturare/iesiri')) {
          tourConfig = {
            target: '#formular_rapid, .card-body',
            title: 'Emitere Factură Fiscală',
            message: '👉 Completează datele clientului și produsele pentru a emite factura!',
            position: 'bottom'
          };
        } else if (route.includes('/facturare')) {
          tourConfig = {
            target: '#tabel_facturi_emise tbody tr:first-child .btn, #tabel_facturi_emise',
            title: 'Facturi Emise & Încasări',
            message: '👉 Folosește butonul cu bancnotă 💵 din dreptul fiecărei facturi pentru a înregistra plăți și încasări.',
            position: 'bottom'
          };
        } else if (route.includes('/firmele-mele')) {
          tourConfig = {
            target: '#btn-conecteaza-anaf, .btn-active-firm-actions, #btn-firma-actions-52314766, .row-firma-cui-52314766 .btn-firma-actions, .row-firma-activa .btn-firma-actions, #tabel_firme tbody tr:first-child .btn-firma-actions',
            title: 'Conectare ANAF SPV',
            message: '👉 În fereastra deschisă pentru firma ta, apasă pe butonul <strong>🔐 Conectează ANAF</strong> din partea de jos pentru conectarea la e-Factura!',
            position: 'top'
          };
        }
      }

      if (tourConfig) {
        try {
          sessionStorage.setItem('pending_guide_tour', JSON.stringify(tourConfig));
        } catch (e) {}
      }

      if (route) {
        const isAlreadyOnFullPath = this.$route && this.$route.fullPath === route;
        const isAlreadyOnBasePath = this.$route && this.$route.path === route.split('?')[0];

        // Emit section event immediately so component activates section even before router hooks
        if (targetSection && window.emitter) {
          window.emitter.emit('set-incarcari-section', targetSection);
        }

        // Emit active firm ANAF modal event immediately if navigating to Firmele Mele
        if ((route.includes('/firmele-mele') || route.includes('action=anaf')) && window.emitter) {
          window.emitter.emit('open-active-firm-anaf');
        }

        if (isAlreadyOnFullPath) {
          setTimeout(() => {
            if (window.startInteractiveTour && tourConfig) {
              window.startInteractiveTour(tourConfig);
            }
          }, 350);
        } else if (isAlreadyOnBasePath) {
          // Same base path (e.g. /incarcari), updating query to ?section=extras
          this.$router.push(route).catch(() => {});
          if (targetSection && window.emitter) {
            window.emitter.emit('set-incarcari-section', targetSection);
          }
          setTimeout(() => {
            if (window.startInteractiveTour && tourConfig) {
              window.startInteractiveTour(tourConfig);
            }
          }, 450);
        } else {
          // Different route path
          this.$router.push(route).then(() => {
            if (targetSection && window.emitter) {
              setTimeout(() => {
                window.emitter.emit('set-incarcari-section', targetSection);
              }, 100);
            }
            if (tourConfig) {
              setTimeout(() => {
                if (window.startInteractiveTour) {
                  window.startInteractiveTour(tourConfig);
                }
              }, 500);
            }
          }).catch(() => {});
        }
      }
    },

    // Întrebări Frecvente & Ghid Logic (Strictly User Focused)
    resetChat() {
      this.chatMessages = [
        {
          sender: 'assistant',
          text: '👋 <strong>Dialog resetat.</strong> Selectează o întrebare frecventă de mai jos sau caută ce dorești să afli:',
          time: 'Acum'
        }
      ];
      this.assistantInput = '';
    },
    submitAssistantQuery() {
      if (!this.assistantInput.trim()) return;
      this.askAssistant(this.assistantInput.trim());
      this.assistantInput = '';
    },
    askAssistant(prompt) {
      this.chatMessages.push({
        sender: 'user',
        text: prompt,
        time: 'Acum'
      });

      this.isBotTyping = true;
      this.$nextTick(() => {
        this.scrollChatToBottom();
      });

      setTimeout(() => {
        const response = this.generateBotResponse(prompt);
        this.chatMessages.push(response);
        this.isBotTyping = false;
        this.$nextTick(() => {
          this.scrollChatToBottom();
        });
      }, 400);
    },
    scrollChatToBottom() {
      const el = this.$refs.chatStreamRef;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    generateBotResponse(query) {
      const q = query.toLowerCase();

      // 1. Finalizare Lună
      if (q.includes('finaliz') || q.includes('terminat') || q.includes('anunt') || (q.includes('inchid') && q.includes('luna'))) {
        return {
          sender: 'assistant',
          text: `Pentru a marca luna ca finalizată și a o preda contabilului:<br>
          1. Mergi la <strong>Documente</strong> din meniu.<br>
          2. Selectează luna dorită din selectorul de sus (ex: August).<br>
          3. Apasă butonul verde <strong>"Am terminat încărcările pe [Luna]"</strong>.<br>
          Luna va fi marcată cu galben <strong>FINALIZAT</strong>, iar contabilul tău va primi o notificare că documentele sunt gata de prelucrare!`,
          actionRoute: '/incarcari?section=primite',
          actionText: 'Mergi la Documente (Finalizare)',
          actionTour: {
            target: '.btn-finalize-month',
            title: 'Finalizare Lună pentru Contabil',
            message: '👉 <strong>Apasă pe acest buton verde</strong> ("Am terminat încărcările") când ai terminat de încărcat actele lunii!',
            position: 'top'
          },
          tips: ['Dacă uiți o factură, poți apăsa oricând "Anulează finalizarea" pentru a redeschide luna.']
        };
      }

      // 2. Extras de cont bancar
      if (q.includes('extras') || q.includes('banca') || q.includes('extrasul') || q.includes('cont bancar')) {
        return {
          sender: 'assistant',
          text: `Pentru a încărca <strong>extrasul bancar</strong>:<br>
          1. Descarcă extrasul de cont din aplicația băncii tale (format <strong>PDF, Excel, MT940 sau CSV</strong>).<br>
          2. Intră în secțiunea <strong>Extras de cont</strong> din modulul Documente.<br>
          3. Alege luna și trage fișierul extrasului în zona de încărcare.<br>
          Contabilul tău va prelua direct extrasul pentru reconcilierea tranzacțiilor!`,
          actionRoute: '/incarcari?section=extras',
          actionText: 'Încarcă Extras de Cont Acum',
          actionTour: {
            target: '#btn-incarcari-extras, .btn-doc.active',
            title: 'Încărcare Extras de Cont',
            message: '👉 Secțiunea <strong>Extras de cont</strong> a fost deschisă automat! Trage aici fișierele PDF sau Excel din online banking.',
            position: 'bottom'
          },
          tips: ['Dacă ai conturi la mai multe bănci, poți încărca extrasele pentru fiecare bancă în aceeași lună.']
        };
      }

      // 3. Facturi Primite / Cumpărări / Furnizori / Cheltuieli
      if (q.includes('cumparar') || q.includes('primite') || q.includes('furnizor') || q.includes('cheltuiel') || q.includes('bon')) {
        return {
          sender: 'assistant',
          text: `Pentru a încărca <strong>facturile de achiziții și cheltuieli</strong>:<br>
          1. Deschide secțiunea <strong>Documente primite / cumpărări</strong>.<br>
          2. Asigură-te că este selectată luna corectă a documentelor.<br>
          3. Trage facturile PDF sau scanate și bonurile fiscale cu CUI.<br>
          Documentele devin disponibile imediat pentru contabilul tău!`,
          actionRoute: '/incarcari?section=primite',
          actionText: 'Încarcă Facturi Cumpărări',
          actionTour: {
            target: '#btn-incarcari-primite, .btn-doc.active',
            title: 'Facturi Primite (Cumpărări)',
            message: '👉 Secțiunea <strong>Documente primite</strong> este activată! Trage facturile de la furnizori aici.',
            position: 'bottom'
          },
          tips: ['Dacă furnizorul a emis factura prin RO e-Factura, aceasta se descarcă automat în sistem la Facturi Intrări!']
        };
      }

      // 4. Culori Luni (Verde, Galben, Roșu)
      if (q.includes('galben') || q.includes('rosu') || q.includes('culor') || q.includes('avertiz') || q.includes('restant')) {
        return {
          sender: 'assistant',
          text: `Iată ce înseamnă culorile lunilor pentru tine ca utilizator:<br>
          • <strong>🟢 Verde (În lucru normal)</strong>: Luna curentă și luna anterioară (M-1). Încarci documente fără avertismente de întârziere.<br>
          • <strong>🟡 Galben (Atenție M-2)</strong>: Luna din urmă cu 2 luni (ex: Iulie când suntem în Septembrie). Se apropie scadența declarațiilor fiscale – contabilul are nevoie urgent de toate documentele!<br>
          • <strong>🟡 Galben (Status FINALIZAT)</strong>: Ai apăsat butonul "Am terminat încărcările". Luna este predată și se află în verificare la contabil.<br>
          • <strong>🔴 Roșu (Restantă M-3+)</strong>: Luni vechi rămase nepredate sau cu documente lipsă. Intră pe luna respectivă, încarcă actele restante și apasă finalizare!`,
          actionRoute: '/incarcari?section=primite',
          actionText: 'Vezi Situația Lunilor',
          actionTour: {
            target: '.btn-finalize-month, #btn-incarcari-primite',
            title: 'Gestiune Lună Documente',
            message: '👉 Aici poți verifica starea lunii și butonul de finalizare pentru contabil!',
            position: 'top'
          },
          tips: ['Predarea actelor până pe 10-15 ale lunii evită avertizările galbene și roșii.']
        };
      }

      // 5. Emitere Factură Nouă
      if (q.includes('factur') && (q.includes('nou') || q.includes('emit') || q.includes('creeaz'))) {
        return {
          sender: 'assistant',
          text: `Pentru a emite o factură fiscală nouă:<br>
          1. Mergi la <strong>Facturare -> + Adaugă Factură</strong>.<br>
          2. Caută clientul după CUI (datele oficiale se descarcă automat de la ANAF).<br>
          3. Adaugă produsele sau serviciile, cota TVA și apasă <strong>Salvează Factura</strong>.<br>
          Factura este gata de trimis pe e-mail clientului și în e-Factura!`,
          actionRoute: '/facturare/iesiri',
          actionText: 'Emite Factură Acum',
          actionTour: {
            target: '#formular_rapid, .card-body',
            title: 'Emitere Factură Fiscală',
            message: '👉 Completează datele clientului și articolele facturii, apoi apasă Salvează Factura!',
            position: 'bottom'
          },
          tips: ['Plaja de numere se incrementează automat conform seriei alese pe firmă.']
        };
      }

      // 6. Conectare ANAF SPV
      if (q.includes('anaf') || q.includes('spv') || q.includes('e-factura')) {
        return {
          sender: 'assistant',
          text: `Conectarea cu <strong>ANAF (SPV)</strong> pentru firma ta se face astfel:<br>
          1. Mergi la <strong>Firmele Mele</strong>.<br>
          2. Apasă pe butonul de <strong>Acțiuni ▾ -> Editare</strong> din dreptul firmei tale.<br>
          3. În fereastra de editare, în <strong>partea de jos (la subsol)</strong>, apasă pe butonul albastru <strong>"🔐 Conectează ANAF"</strong>.<br>
          Vei fi redirecționat securizat pe portalul oficial ANAF pentru autorizarea cu certificatul digital!`,
          actionRoute: '/firmele-mele?action=anaf',
          actionText: 'Deschide Conectare ANAF pentru firma ta',
          actionTour: {
            target: '#btn-conecteaza-anaf, .btn-active-firm-actions, #btn-firma-actions-52314766, .row-firma-cui-52314766 .btn-firma-actions, .row-firma-activa .btn-firma-actions, #tabel_firme tbody tr:first-child .btn-firma-actions',
            title: 'Conectare ANAF SPV',
            message: '👉 În fereastra deschisă pentru firma ta, apasă pe butonul <strong>🔐 Conectează ANAF</strong> din partea de jos pentru conectarea cu certificatul digital!',
            position: 'top'
          },
          tips: ['Data de expirare a token-ului va fi afișată verde în tabel. Când expiră, apeși "♻️ Refresh Token".']
        };
      }

      // 7. Încasări & Plăți / Chitanțe
      if (q.includes('incas') || q.includes('chitanta') || q.includes('plata') || q.includes('op')) {
        return {
          sender: 'assistant',
          text: `Pentru a înregistra o încasare pe o factură emisă:<br>
          1. Mergi la <strong>Facturi Emise</strong>.<br>
          2. Apasă pe iconița verde cu bancnotă <strong>💵</strong> din dreptul facturii plătite.<br>
          3. Alege modalitatea: <strong>Chitanță</strong> (numerar) sau <strong>Ordin de plată</strong> (bancă).<br>
          4. Introdu suma încasată (integrală sau parțială) și salvează!`,
          actionRoute: '/facturare',
          actionText: 'Vezi Facturi Emise',
          actionTour: {
            target: '#tabel_facturi_emise tbody tr:first-child .btn, #tabel_facturi_emise',
            title: 'Facturi Emise & Încasări',
            message: '👉 Folosește iconița cu bancnotă 💵 din dreptul fiecărei facturi pentru a înregistra plăți și chitanțe.',
            position: 'bottom'
          },
          tips: ['Dacă o factură este plătită doar parțial, soldul rămas este evidențiat automat cu galben.']
        };
      }

      // Fallback
      return {
        sender: 'assistant',
        text: `Am găsit ghiduri utile pentru tine în secțiunile noastre!<br>
        Alege una dintre temele recomandate de mai jos sau folosește meniul din stânga pentru tutoriale detaliate pas-cu-pas.`,
        actionRoute: '/incarcari?section=primite',
        actionText: 'Deschide Modulul Documente',
        tips: ['Poți selecta tab-ul "Ghiduri Detaliate" pentru tutoriale complete pas-cu-pas.']
      };
    },

    // Checklist Persistence
    loadChecklistProgress() {
      try {
        const saved = localStorage.getItem('conta2go_checklist') || localStorage.getItem('artprime_conta_checklist');
        if (saved) {
          const parsed = JSON.parse(saved);
          this.checklistItems.forEach(item => {
            if (parsed[item.id] !== undefined) {
              item.done = parsed[item.id];
            }
          });
        }
      } catch (e) {
        console.error('Checklist storage error:', e);
      }
    },
    saveChecklistProgress() {
      try {
        const state = {};
        this.checklistItems.forEach(item => {
          state[item.id] = item.done;
        });
        localStorage.setItem('conta2go_checklist', JSON.stringify(state));

        if (this.checklistProgress === 100) {
          if (window.Swal) {
            window.Swal.fire({
              icon: 'success',
              title: 'Felicitări!',
              text: 'Ai parcurs toți pașii din checklist-ul lunar! Documentele tale sunt gata pentru contabilitate.',
              timer: 3500,
              showConfirmButton: false
            });
          }
        }
      } catch (e) {
        console.error('Checklist save error:', e);
      }
    },
    resetChecklist() {
      this.checklistItems.forEach(i => i.done = false);
      this.saveChecklistProgress();
    },

    // Calculator Actions
    copyCalcResult() {
      const text = `Baza Net: ${this.formatMoney(this.calcResults.net)} RON | TVA (${this.calcVatRate}%): ${this.formatMoney(this.calcResults.vat)} RON | Total: ${this.formatMoney(this.calcResults.total)} RON`;
      navigator.clipboard.writeText(text).then(() => {
        if (window.Swal) {
          window.Swal.fire({
            icon: 'success',
            title: 'Copiat!',
            text: text,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          alert('Rezultatul a fost copiat în clipboard!');
        }
      });
    },

    // Support Form
    sendSupportMessage() {
      if (!this.contactForm.subiect || !this.contactForm.mesaj) {
        if (window.Swal) {
          window.Swal.fire('Eroare', 'Vă rugăm să completați subiectul și mesajul.', 'warning');
        } else {
          alert('Vă rugăm să completați subiectul și mesajul.');
        }
        return;
      }

      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Solicitare trimisă!',
          text: 'Mesajul tău a fost înregistrat. Un membru al echipei de asistență te va contacta în cel mai scurt timp.',
          timer: 3500,
          showConfirmButton: true
        });
      } else {
        alert('Solicitarea a fost trimisă cu succes!');
      }

      this.contactForm.subiect = '';
      this.contactForm.mesaj = '';
      this.closeModal();
    }
  }
};
</script>

<style scoped>
/* Gradient Header */
.bg-gradient-help {
  background: linear-gradient(135deg, #0d1527 0%, #1e3a8a 55%, #0284c7 100%);
}

.help-modal-container {
  height: 82vh;
  max-height: 680px;
  display: flex;
  flex-direction: column;
}

.help-header-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.help-header-search::placeholder {
  color: rgba(255, 255, 255, 0.6) !important;
}

.help-body-flex {
  height: calc(82vh - 105px);
  max-height: 575px;
  overflow: hidden;
}

/* Sidebar Navigation */
.help-sidebar-menu {
  width: 290px;
  min-width: 290px;
  overflow-y: auto;
}

.help-sidebar-menu .nav-pills .nav-link {
  padding: 11px 15px !important;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: flex !important;
  align-items: center !important;
}

.nav-icon-box {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  max-width: 36px !important;
  border-radius: 9px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  margin-right: 14px !important;
}

.menu-item-title {
  font-size: 0.88rem !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
  color: #1e293b;
  margin-bottom: 3px !important;
  display: block !important;
}

.menu-item-subtitle {
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  line-height: 1.25 !important;
  color: #64748b;
  display: block !important;
}

.nav-pills .nav-link:hover:not(.active) {
  background-color: rgba(0, 158, 247, 0.08);
}
.nav-pills .nav-link:hover:not(.active) .menu-item-title {
  color: #009ef7;
}
.nav-pills .nav-link.active {
  background: linear-gradient(135deg, #009ef7 0%, #0077c5 100%);
  color: #ffffff !important;
  box-shadow: 0 3px 10px rgba(0, 158, 247, 0.35);
  border-color: transparent;
}
.nav-pills .nav-link.active .menu-item-title {
  color: #ffffff !important;
}
.nav-pills .nav-link.active .menu-item-subtitle {
  color: rgba(255, 255, 255, 0.85) !important;
}
.nav-pills .nav-link.active .nav-icon-box {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
}

/* Chat Stream & Bubbles */
.chat-stream {
  background: #f8fafc;
}

.chat-row {
  display: flex !important;
  align-items: flex-start !important;
  gap: 12px !important;
  width: 100% !important;
}

/* Fix Avatar: Strictly 36x36 circular, never stretched! */
.chat-avatar {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  min-height: 36px !important;
  max-width: 36px !important;
  max-height: 36px !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  align-self: flex-start !important;
}

.chat-bubble-bot {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  border-top-left-radius: 3px;
  padding: 12px 16px;
  color: #1e293b;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  max-width: 82%;
  line-height: 1.55;
}

.chat-bubble-user {
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  border-radius: 14px;
  border-top-right-radius: 3px;
  padding: 10px 16px;
  box-shadow: 0 3px 10px rgba(2, 132, 199, 0.25);
  max-width: 80%;
  line-height: 1.55;
}

/* Prompt chips horizontal scroll */
.prompt-scroll {
  scrollbar-width: thin;
}
.prompt-scroll::-webkit-scrollbar {
  height: 4px;
}
.prompt-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
}

.hover-scale:hover {
  transform: scale(1.05);
  transition: transform 0.15s ease-in-out;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-elevate-up {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-elevate-up:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08) !important;
}

.shadow-2xs {
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

/* === Dark Mode Adjustments === */
[data-bs-theme="dark"] .help-modal-container {
  background-color: #151521 !important;
}

[data-bs-theme="dark"] .help-sidebar-menu {
  background-color: #1a1a27 !important;
  border-color: #2b2b40 !important;
}

[data-bs-theme="dark"] .menu-item-title {
  color: #e1e3ea !important;
}

[data-bs-theme="dark"] .menu-item-subtitle {
  color: #92929f !important;
}

[data-bs-theme="dark"] .nav-pills .nav-link:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

[data-bs-theme="dark"] .nav-pills .nav-link.active .menu-item-title {
  color: #ffffff !important;
}

[data-bs-theme="dark"] .nav-pills .nav-link.active .menu-item-subtitle {
  color: rgba(255, 255, 255, 0.85) !important;
}

[data-bs-theme="dark"] .chat-stream {
  background-color: #12121e !important;
}

[data-bs-theme="dark"] .chat-bubble-bot {
  background-color: #1e1e2d !important;
  border-color: #2b2b40 !important;
  color: #e1e3ea !important;
}

[data-bs-theme="dark"] .chat-suggestions {
  background-color: #181824 !important;
  border-color: #2b2b40 !important;
}

[data-bs-theme="dark"] .chat-suggestions .btn-white {
  background-color: #222234 !important;
  border-color: #323248 !important;
  color: #e1e3ea !important;
}

[data-bs-theme="dark"] .chat-input-bar {
  background-color: #1a1a27 !important;
  border-color: #2b2b40 !important;
}

[data-bs-theme="dark"] .bg-body {
  background-color: #1e1e2d !important;
  border-color: #2b2b40 !important;
}

[data-bs-theme="dark"] .text-gray-900 {
  color: #f1f1f4 !important;
}

[data-bs-theme="dark"] .text-gray-800 {
  color: #d8d8e4 !important;
}

[data-bs-theme="dark"] .text-gray-700 {
  color: #b5b5c3 !important;
}

[data-bs-theme="dark"] .text-gray-600 {
  color: #92929f !important;
}

[data-bs-theme="dark"] .modal-footer {
  background-color: #1a1a27 !important;
  border-color: #2b2b40 !important;
}

.shortcut-key {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  font-size: 0.76rem;
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a !important; /* High-contrast dark text */
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-bottom: 2px solid #94a3b8 !important; /* 3D tactile keyboard keycap effect */
  border-radius: 5px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin: 0 0.25rem;
  vertical-align: middle;
}

[data-bs-theme="dark"] .shortcut-key {
  color: #f8fafc !important;
  background-color: #1e293b !important;
  border-color: #475569 !important;
  border-bottom-color: #64748b !important;
}
</style>
